import { useState, useEffect } from 'react';
import { TruckIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { useVans, useVanStock } from '../hooks/useVanStock';

function Chip({ label, value, tone = 'gray' }) {
  const tones = { gray: 'bg-gray-100 text-gray-700', amber: 'bg-amber-100 text-amber-800', rose: 'bg-rose-100 text-rose-800', emerald: 'bg-emerald-100 text-emerald-800' };
  return (
    <div className={`rounded-lg px-3 py-2 ${tones[tone]}`}>
      <div className="text-lg font-bold tabular-nums">{value}</div>
      <div className="text-xs font-medium">{label}</div>
    </div>
  );
}

function VanStock() {
  const { data: vans, isLoading: vansLoading } = useVans();
  const [userId, setUserId] = useState('');
  const [q, setQ] = useState('');
  const { data, isLoading, isError } = useVanStock(userId);

  // Auto-select the first van once the list loads.
  useEffect(() => { if (!userId && vans && vans.length) setUserId(String(vans[0].user_id)); }, [vans, userId]);

  const rows = (data?.rows || []).filter((r) => {
    if (!q) return true;
    const s = q.toLowerCase();
    return (r.name || '').toLowerCase().includes(s) || (r.sku || '').toLowerCase().includes(s);
  });

  const rowClass = (r) => (r.on_hand < 0 ? 'bg-rose-50' : r.on_hand === 0 ? 'bg-amber-50/60' : '');
  const onHandCell = (r) => {
    if (r.on_hand < 0) return 'text-rose-700 font-semibold';
    if (r.on_hand === 0) return 'text-amber-700 font-semibold';
    if (r.on_hand <= 2) return 'text-amber-600';
    return 'text-gray-900';
  };

  const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-GB') : '—');

  return (
    <div className="p-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold text-gray-900"><TruckIcon className="h-6 w-6 text-brand" /> Van Stock</h1>
          <p className="mt-1 text-sm text-gray-500">ISP stock per van — the latest weekly count minus stock used on job cards since. Updates as engineers log install stock in the app.</p>
        </div>
        <div className="flex items-center gap-2">
          <select value={userId} onChange={(e) => setUserId(e.target.value)} className="rounded-md border border-gray-300 px-3 py-2 text-sm">
            {vansLoading && <option>Loading…</option>}
            {(vans || []).map((v) => <option key={v.user_id} value={v.user_id}>{v.display_name} — {v.van_registration || 'no reg'} ({v.region || '—'})</option>)}
          </select>
        </div>
      </div>

      {data?.van && (
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <span className="rounded-full bg-gray-100 px-3 py-1 text-sm text-gray-700">Van <span className="font-semibold">{data.van.van_registration || '—'}</span></span>
          <span className="rounded-full bg-gray-100 px-3 py-1 text-sm text-gray-700">{data.van.region || '—'}</span>
          <span className="rounded-full bg-gray-100 px-3 py-1 text-sm text-gray-700">Last count <span className="font-semibold">{fmtDate(data.van.count_date)}</span></span>
          <Chip label="SKUs" value={data.totals.lines} />
          <Chip label="Low (≤2)" value={data.totals.low} tone="amber" />
          <Chip label="Out (0)" value={data.totals.out} tone="amber" />
          <Chip label="Over-used" value={data.totals.discrepancies} tone="rose" />
        </div>
      )}

      <div className="relative mt-4 max-w-sm">
        <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search item or SKU…" className="w-full rounded-md border border-gray-300 py-2 pl-9 pr-3 text-sm" />
      </div>

      <div className="mt-3 overflow-x-auto rounded-lg border border-gray-200 bg-white">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
            <tr>
              <th className="px-4 py-2.5">Item</th><th className="px-4 py-2.5">Category</th>
              <th className="px-4 py-2.5 text-right">Counted</th><th className="px-4 py-2.5 text-right">Used since</th><th className="px-4 py-2.5 text-right">On hand</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {isLoading && <tr><td colSpan={5} className="px-4 py-6 text-center text-gray-400">Loading…</td></tr>}
            {isError && <tr><td colSpan={5} className="px-4 py-6 text-center text-rose-500">Could not load van stock.</td></tr>}
            {!isLoading && !isError && rows.length === 0 && <tr><td colSpan={5} className="px-4 py-6 text-center text-gray-400">No stock lines.</td></tr>}
            {rows.map((r) => (
              <tr key={r.stock_item_id} className={rowClass(r)}>
                <td className="px-4 py-2.5 text-gray-800">{r.name}<span className="ml-2 font-mono text-xs text-gray-400">{r.sku}</span></td>
                <td className="whitespace-nowrap px-4 py-2.5 text-gray-500">{r.category || '—'}</td>
                <td className="px-4 py-2.5 text-right tabular-nums text-gray-600">{r.counted}</td>
                <td className="px-4 py-2.5 text-right tabular-nums text-gray-600">{r.used || 0}</td>
                <td className={`px-4 py-2.5 text-right tabular-nums ${onHandCell(r)}`}>{r.on_hand}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="mt-2 text-xs text-gray-400">On hand = counted − used since the count. Negative (red) = more used than counted (missed count or discrepancy).</p>
    </div>
  );
}

export default VanStock;
